import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { renderTemplate, type RenderedElement } from './renderer.js';

const DPI = 203;
const MM_PER_INCH = 25.4;
const mmToDots = (mm: number): number => Math.round((mm / MM_PER_INCH) * DPI);

const escapeCpcl = (s: string): string => s.replace(/\r|\n/g, ' ');

const pickCpclFont = (pt: number | undefined): { font: number; size: number } => {
  if (!pt) return { font: 4, size: 0 };
  if (pt >= 20) return { font: 7, size: 0 };
  if (pt >= 14) return { font: 5, size: 0 };
  if (pt >= 10) return { font: 4, size: 0 };
  return { font: 0, size: 0 };
};

const pickRotationCommand = (rotation: 0 | 90 | 180 | 270): string => {
  switch (rotation) {
    case 90:
      return 'T90';
    case 180:
      return 'T180';
    case 270:
      return 'T270';
    case 0:
    default:
      return 'T';
  }
};

const buildText = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const { font, size } = pickCpclFont(el.font_size_pt);
  const cmd = pickRotationCommand(el.rotation);
  const text = escapeCpcl(el.text ?? '');
  const bold = el.font_weight === 'bold' ? 'SETBOLD 2\r\n' : '';
  const unbold = el.font_weight === 'bold' ? '\r\nSETBOLD 0' : '';
  return `${bold}${cmd} ${font} ${size} ${x} ${y} ${text}${unbold}`;
};

const buildBarcode = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const h = el.h_mm ? mmToDots(el.h_mm) : 60;
  const data = escapeCpcl(el.barcode_data ?? '');
  const orient = el.rotation === 90 || el.rotation === 270 ? 'VBARCODE' : 'BARCODE';
  return `${orient} 128 1 1 ${h} ${x} ${y} ${data}`;
};

const buildQr = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const data = escapeCpcl(el.qr_data ?? '');
  return `BARCODE QR ${x} ${y} M 2 U 4\r\nMA,${data}\r\nENDQR`;
};

const buildLine = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const x2 = x + mmToDots(el.w_mm ?? 0);
  const y2 = y + Math.max(1, mmToDots(el.h_mm ?? 0.3));
  const thick = Math.max(1, mmToDots(el.h_mm ?? 0.3));
  return `LINE ${x} ${y} ${x2} ${y2} ${thick}`;
};

const buildRect = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm);
  const y = mmToDots(el.y_mm);
  const x2 = x + mmToDots(el.w_mm ?? 0);
  const y2 = y + mmToDots(el.h_mm ?? 0);
  return `BOX ${x} ${y} ${x2} ${y2} 2`;
};

export const buildCpcl = (
  template: LabelTemplate,
  data: Record<string, unknown>,
  copies: number = 1,
): string => {
  const rendered = renderTemplate(template, data);
  const heightDots = mmToDots(template.height_mm);
  const widthDots = mmToDots(template.width_mm);
  const lines: string[] = [];
  lines.push(`! 0 200 200 ${heightDots} ${Math.max(1, copies)}`);
  lines.push(`PAGE-WIDTH ${widthDots}`);
  for (const el of rendered) {
    switch (el.type) {
      case 'text':
        lines.push(buildText(el));
        break;
      case 'barcode':
        lines.push(buildBarcode(el));
        break;
      case 'qr':
        lines.push(buildQr(el));
        break;
      case 'line':
        lines.push(buildLine(el));
        break;
      case 'rect':
        lines.push(buildRect(el));
        break;
      case 'expiry_weekday':
        throw new Error('Il giorno di scadenza richiede la stampa raster: seleziona un driver compatibile.');
      case 'image':
        break;
      default:
        break;
    }
  }
  lines.push('PRINT');
  return lines.join('\r\n') + '\r\n';
};

export class CpclAdapter implements PrinterAdapter {
  readonly driver = 'cpcl' as const;

  async discover(): Promise<PrinterDevice[]> {
    // TODO: BLE scan for Zebra QLn/ZQ mobile printers.
    return [];
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    // TODO: open BLE SPP channel.
    return { deviceId: device.id, driver: this.driver };
  }

  async disconnect(_session: PrinterSession): Promise<void> {
    // TODO: close BLE channel.
  }

  async printLabel(
    _session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    const cpcl = buildCpcl(template, data, copies);
    // TODO: write bytes to transport.
    void cpcl;
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    // TODO: send `! U1 getvar "device.status"` and parse.
    return {
      online: true,
      paper_ok: true,
      cover_closed: true,
      battery_pct: null,
      error_code: null,
    };
  }
}
